import { inject } from '@angular/core';
import { CanMatchFn, RedirectCommand, ResolveFn, Router } from '@angular/router';

import { TenantState } from '@core/models/tenant.state';
import { TenantScopeService } from '@core/services/tenant-scope.service';

/**
 * Matches `/` only when the request arrived on a tenant's own subdomain
 * (SM-02), so that origin's root serves the tenant storefront instead of the
 * marketplace home. On every other host this returns false and the router
 * falls through to the marketplace home route.
 *
 * The scope is already resolved by the time routing runs - `TenantHostService`
 * enters it during app initialization, before the first render.
 */
export const hostTenantMatch: CanMatchFn = () => inject(TenantScopeService).isHostScope();

/**
 * Hands the host-resolved tenant to `StorefrontHomePageComponent` under the
 * same `tenant` key the path-based resolver uses, so the page is identical on
 * `mattsfinds.{baseDomain}/` and `/retailer/mattsfinds`.
 */
export const hostTenantResolver: ResolveFn<TenantState> = () => {
  const scope = inject(TenantScopeService);
  const tenant = scope.tenant();

  // Unreachable while `hostTenantMatch` gates the route; kept because a null
  // tenant here would otherwise surface as a broken required input.
  if (tenant === null) {
    return new RedirectCommand(inject(Router).parseUrl('/'));
  }

  return tenant;
};
